/**
 * Frontend observability — Sentry (errors) + PostHog (product analytics).
 *
 * Both are env-gated: with no key they are hard no-ops (SDK never initializes).
 *
 * Privacy posture is CONSERVATIVE because tenants enter real PII (names,
 * addresses) in the operator dashboard and tenant portal:
 *   - Sentry: sendDefaultPii false, NO Session Replay (the biggest PII risk).
 *   - PostHog: session recording disabled, autocapture keeps element/click
 *     data only (PostHog never captures input values without recording),
 *     person profiles for identified users only, and a sanitizer strips
 *     PII-looking keys from any custom event properties.
 */

import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || '';
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const POSTHOG_HOST =
  process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const ENVIRONMENT = process.env.REACT_APP_ENV || 'production';

let posthogReady = false;

// Keys we never want to leave the browser in analytics properties.
const PII_KEYS = [
  'email',
  'phone',
  'password',
  'full_name',
  'name',
  'address',
  'city',
  'state',
  'zip',
  'zip_code',
  'card',
  'client_secret',
  'token',
  'ssn',
];

const sanitizeProperties = (properties) => {
  if (!properties || typeof properties !== 'object') return properties;
  const clean = {};
  for (const [k, v] of Object.entries(properties)) {
    const lk = k.toLowerCase();
    clean[k] = PII_KEYS.some((p) => lk.includes(p)) ? '[redacted]' : v;
  }
  return clean;
};

export function initObservability() {
  if (SENTRY_DSN) {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: ENVIRONMENT,
      sendDefaultPii: false,
      // No Session Replay integration on purpose — it would record PII forms.
      tracesSampleRate: 0.1,
      beforeSend(event) {
        // Strip request bodies / cookies if any integration attached them.
        if (event.request) {
          delete event.request.cookies;
          delete event.request.data;
        }
        return event;
      },
    });
  }

  if (POSTHOG_KEY) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      disable_session_recording: true,
      autocapture: true, // clicks/pageviews only — never input values
      capture_pageview: true,
      sanitize_properties: sanitizeProperties,
    });
    posthogReady = true;
  }
}

/** Capture a product-funnel event (no-op if PostHog disabled). */
export function track(eventName, properties = {}) {
  if (posthogReady) {
    posthog.capture(eventName, sanitizeProperties(properties));
  }
}

/** Associate events with a stable user id (never PII). */
export function identify(distinctId) {
  if (posthogReady && distinctId) {
    posthog.identify(String(distinctId));
  }
}

/** Clear identity on logout. */
export function resetIdentity() {
  if (posthogReady) {
    posthog.reset();
  }
}

/**
 * Report a render crash to the backend (operation_failures, severity
 * critical). The error boundary's "Something went wrong" screen was a
 * dead end: render crashes pass every push gate (build, jsdom, smoke)
 * because they need prod data/state, and with no Sentry DSN nothing
 * recorded them — the 2026-08-13 /send-esign report arrived with zero
 * diagnostics. Raw fetch, not apiClient: the reporter must not depend
 * on interceptors that may themselves be mid-failure. Never throws.
 */
export function reportClientError(error, componentStack) {
  try {
    const base = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';
    const token = localStorage.getItem('accessToken');
    if (!token) return; // endpoint is auth'd; unauthenticated crashes skip
    fetch(`${base}/api/client-errors`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        message: String((error && error.message) || error).slice(0, 900),
        stack: String((error && error.stack) || '').slice(0, 7500),
        component_stack: String(componentStack || '').slice(0, 7500),
        route: `${window.location.pathname}${window.location.search}`.slice(0, 480),
        user_agent: navigator.userAgent.slice(0, 280),
      }),
    }).catch(() => {});
  } catch {
    /* reporting must never throw */
  }
}

/** Sentry's boundary + self-reporting to our own backend on every catch. */
export const ErrorBoundary = (props) => (
  <Sentry.ErrorBoundary
    {...props}
    onError={(error, componentStack, eventId) => {
      reportClientError(error, componentStack);
      if (props.onError) props.onError(error, componentStack, eventId);
    }}
  />
);
